import React, { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Badge,
  Toolbar,
  Typography,
  Button,
  Box,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import ExtensionIcon from '@mui/icons-material/Extension';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExploreIcon from '@mui/icons-material/Explore';
import ReplayIcon from '@mui/icons-material/Replay';
import GridViewIcon from '@mui/icons-material/GridView';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../auth/AuthContext';
import ReviewMenu from '../review/ReviewMenu';
import { useReviewCounts } from '../review/useReviewCounts';
import { explorerRootPath } from '../explorer/explorerUrl';
import { isReplayPath, replayHubPath } from '../replay/replayUrl';
import { coursesRootPath, isCoursesPath } from '../courses/coursesUrl';
import {
  GAMES_BROWSE_PATH,
  MY_GAMES_PATH,
} from '../games/shared/gamesBrowseUrl';
import {
  APP_BAR_TOOLBAR_MIN_HEIGHT,
  DRAWER_NAV_ITEM_FONT_SIZE,
  DRAWER_NAV_ITEM_ICON_MIN_WIDTH,
  DRAWER_NAV_ITEM_ICON_PX,
  DRAWER_NAV_ITEM_MIN_HEIGHT,
  DRAWER_NAV_ITEM_PY,
  DRAWER_NAV_BADGE_FONT_SIZE,
  DRAWER_NAV_BADGE_OFFSET,
  DRAWER_NAV_BADGE_SIZE_PX,
  DRAWER_NAV_MENU_BUTTON_PX,
  DRAWER_NAV_MENU_ICON_PX,
  DRAWER_NAV_PANEL_WIDTH_PX,
} from './appLayoutConstants';
import { buildLoginPath } from '../common/authRedirect';
import {
  matchNavPath,
  drawerBrandLogoSx,
  navBrandLogoSx,
  navMenuItemsSx,
  navSectionDividerSx,
  navTabGroupSx,
  navTabLeadingIconSx,
  navTabIconSx,
  navTabSx,
  navToolbarSx,
  BRAND_GOLD,
} from './navTabStyles';
import NavTab from './NavTab';
import { useMeasuredElementHeight } from './useMeasuredElementHeight';

const brandLogoContainerSx = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: { xs: 1, sm: 1.5, lg: 0.75, xl: 1, xxl: 2 },
  mr: { lg: 0.5, xl: 1, xxl: 3 },
};

const drawerPaperSx = {
  bgcolor: 'grey.900',
  color: 'common.white',
} as const;

const drawerNavBadgeSx = {
  '& .MuiBadge-badge': {
    minWidth: DRAWER_NAV_BADGE_SIZE_PX,
    height: DRAWER_NAV_BADGE_SIZE_PX,
    fontSize: DRAWER_NAV_BADGE_FONT_SIZE,
    fontWeight: 700,
    right: DRAWER_NAV_BADGE_OFFSET,
    top: DRAWER_NAV_BADGE_OFFSET,
    px: { xs: 0.5, sm: 0.625, md: 0.75 },
  },
} as const;

const drawerItemButtonSx = (active = false) => ({
  color: active ? BRAND_GOLD : 'common.white',
  textDecoration: 'none',
  minHeight: DRAWER_NAV_ITEM_MIN_HEIGHT,
  px: { xs: 2, sm: 2.5, md: 3 },
  py: DRAWER_NAV_ITEM_PY,
  touchAction: 'manipulation',
  WebkitTapHighlightColor: 'transparent',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
  '& .MuiListItemText-primary': {
    color: active ? BRAND_GOLD : 'common.white',
    fontWeight: active ? 600 : 400,
    fontSize: DRAWER_NAV_ITEM_FONT_SIZE,
    lineHeight: 1.35,
  },
});

const REVIEW_BADGE_MAX = 9999;

type DrawerNavItemProps = {
  to?: string;
  label: string;
  icon: React.ReactElement;
  active?: boolean;
  badgeContent?: number;
  onClick?: () => void;
  onNavigate?: () => void;
};

const drawerItemIconSx = (active: boolean) => ({
  minWidth: DRAWER_NAV_ITEM_ICON_MIN_WIDTH,
  color: 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: DRAWER_NAV_ITEM_ICON_PX,
  },
  ...navTabIconSx(active),
});

const DrawerNavItem = ({
  to,
  label,
  icon,
  active = false,
  badgeContent,
  onClick,
  onNavigate,
}: DrawerNavItemProps) => {
  const handleClick = () => {
    onClick?.();
    onNavigate?.();
  };

  const iconNode =
    badgeContent != null ? (
      <Badge
        color="error"
        max={REVIEW_BADGE_MAX}
        badgeContent={badgeContent}
        overlap="circular"
        sx={drawerNavBadgeSx}
      >
        {icon}
      </Badge>
    ) : (
      icon
    );

  const content = (
    <>
      <ListItemIcon sx={drawerItemIconSx(active)}>{iconNode}</ListItemIcon>
      <ListItemText primary={label} />
    </>
  );

  if (to) {
    return (
      <ListItemButton
        component={Link}
        to={to}
        onClick={handleClick}
        sx={drawerItemButtonSx(active)}
      >
        {content}
      </ListItemButton>
    );
  }

  return (
    <ListItemButton onClick={handleClick} sx={drawerItemButtonSx(active)}>
      {content}
    </ListItemButton>
  );
};

const BrandLogo = ({ compact = false }: { compact?: boolean }) => {
  const { isGuest, authChecked } = useAuth();

  return (
    <Box sx={brandLogoContainerSx}>
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/endchess-logo.png`}
        alt="EndChess"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        sx={compact ? drawerBrandLogoSx : navBrandLogoSx}
      />
      {authChecked && isGuest && (
        <Typography
          variant="body2"
          sx={{
            color: BRAND_GOLD,
            fontWeight: 600,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          Guest
        </Typography>
      )}
    </Box>
  );
};

const TopMenu: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { pathname, search } = location;
  const returnTo = `${pathname}${search}`;
  const onLogin = pathname === '/' || matchNavPath(pathname, '/login');
  const loginPath = onLogin ? '/login' : buildLoginPath(returnTo);
  // Drawer below desktopMin; horizontal nav from iPad Pro 11" landscape (1194px) up.
  const useDrawerNav = useMediaQuery(theme.breakpoints.down('lg'));
  const appBarRef = useRef<HTMLDivElement>(null);
  const appBarHeight = useMeasuredElementHeight(appBarRef);
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const fallbackAppBarHeight = isUltraWide
    ? 80
    : isWideDesktop
      ? 72
      : isLargeDesktop
        ? 60
        : isSmUp
          ? 52
          : 48;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isLoggedIn, isGuest, authChecked, logout } = useAuth();
  const { counts: reviewCounts, loading: reviewCountsLoading } = useReviewCounts();
  const onExplorer = matchNavPath(pathname, '/explorer');
  const onReplay = isReplayPath(pathname);
  const onMyGames = matchNavPath(pathname, MY_GAMES_PATH);
  const onGamesBrowse = matchNavPath(pathname, GAMES_BROWSE_PATH);
  const onCourses = isCoursesPath(pathname);
  const onPuzzles = matchNavPath(pathname, '/puzzles');
  const onDashboard = matchNavPath(pathname, '/dashboard');
  const onSettings = matchNavPath(pathname, '/settings');
  const onStats = matchNavPath(pathname, '/stats');
  const onContact = matchNavPath(pathname, '/contact');
  const onReview = matchNavPath(pathname, '/review');
  const replayUrl = replayHubPath();
  const explorerLinkTarget = explorerRootPath();
  const reviewBadgeContent =
    authChecked && !reviewCountsLoading && reviewCounts.all > 0
      ? reviewCounts.all
      : undefined;
  const myGamesNavTarget = MY_GAMES_PATH;

  const authNavItem = authChecked ? (
    isLoggedIn && !isGuest ? (
      <Box component="span" sx={{ ...navTabGroupSx, flexShrink: 0 }}>
        <LogoutIcon sx={navTabLeadingIconSx(false)} />
        <Button color="inherit" onClick={() => void logout()} sx={navTabSx(false)}>
          <Typography>Logout</Typography>
        </Button>
      </Box>
    ) : (
      <Box component="span" sx={{ flexShrink: 0 }}>
        <NavTab
          to={loginPath}
          label="Login"
          icon={LoginIcon}
          active={onLogin}
        />
      </Box>
    )
  ) : null;

  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const menuItems = (
    <Box sx={navMenuItemsSx}>
      <NavTab to="/dashboard" label="Dashboard" icon={DashboardIcon} active={onDashboard} />
      <Divider orientation="vertical" flexItem sx={navSectionDividerSx} />
      <ReviewMenu />
      <Divider orientation="vertical" flexItem sx={navSectionDividerSx} />
      <NavTab to={coursesRootPath()} label="Courses" icon={MenuBookIcon} active={onCourses} />
      <Divider orientation="vertical" flexItem sx={navSectionDividerSx} />
      <NavTab
        to={explorerLinkTarget}
        label="Explorer"
        icon={ExploreIcon}
        active={onExplorer}
      />
      <NavTab
        to={myGamesNavTarget}
        label="My Games"
        icon={PersonOutlineIcon}
        active={onMyGames}
      />
      <NavTab
        to={GAMES_BROWSE_PATH}
        label="Browse"
        icon={GridViewIcon}
        active={onGamesBrowse}
      />
      <NavTab to={replayUrl} label="Replay" icon={ReplayIcon} active={onReplay} />
      <Divider orientation="vertical" flexItem sx={navSectionDividerSx} />
      <NavTab to="/puzzles" label="Puzzles" icon={ExtensionIcon} active={onPuzzles} />
      <Divider orientation="vertical" flexItem sx={navSectionDividerSx} />
      <NavTab
        to="/settings"
        label="Settings"
        icon={SettingsIcon}
        active={onSettings}
        iconOnly
      />
      <NavTab
        to="/stats"
        label="Stats"
        icon={BarChartOutlinedIcon}
        active={onStats}
        iconOnly
      />
      <NavTab
        to="/contact"
        label="Contact"
        icon={MailOutlineIcon}
        active={onContact}
        iconOnly
      />
      {authNavItem}
    </Box>
  );

  const drawerMenuItems = (
    <Box
      component="nav"
      sx={{ width: DRAWER_NAV_PANEL_WIDTH_PX, height: '100%', ...drawerPaperSx }}
    >
      <List disablePadding>
        <DrawerNavItem
          to="/dashboard"
          label="Dashboard"
          icon={<DashboardIcon />}
          active={onDashboard}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to="/review"
          label="Review"
          icon={<SchoolIcon />}
          active={onReview}
          badgeContent={reviewBadgeContent}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to={coursesRootPath()}
          label="Courses"
          icon={<MenuBookIcon />}
          active={onCourses}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to={explorerLinkTarget}
          label="Explorer"
          icon={<ExploreIcon />}
          active={onExplorer}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to={myGamesNavTarget}
          label="My Games"
          icon={<PersonOutlineIcon />}
          active={onMyGames}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to={GAMES_BROWSE_PATH}
          label="Browse"
          icon={<GridViewIcon />}
          active={onGamesBrowse}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to={replayUrl}
          label="Replay"
          icon={<ReplayIcon />}
          active={onReplay}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to="/puzzles"
          label="Puzzles"
          icon={<ExtensionIcon />}
          active={onPuzzles}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to="/settings"
          label="Settings"
          icon={<SettingsIcon />}
          active={onSettings}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to="/stats"
          label="Stats"
          icon={<BarChartOutlinedIcon />}
          active={onStats}
          onNavigate={closeDrawer}
        />
        <DrawerNavItem
          to="/contact"
          label="Contact"
          icon={<MailOutlineIcon />}
          active={onContact}
          onNavigate={closeDrawer}
        />
        {authChecked ? (
          isLoggedIn && !isGuest ? (
            <DrawerNavItem
              label="Logout"
              icon={<LogoutIcon />}
              onClick={() => {
                void logout();
                closeDrawer();
              }}
            />
          ) : (
            <DrawerNavItem
              to={loginPath}
              label="Login"
              icon={<LoginIcon />}
              active={onLogin}
              onNavigate={closeDrawer}
            />
          )
        ) : null}
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexShrink: 0, width: '100%', maxWidth: '100%' }}>
      <AppBar
        ref={appBarRef}
        position="fixed"
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.appBar,
          width: '100%',
          maxWidth: '100%',
          left: 0,
          right: 0,
          boxSizing: 'border-box',
        }}
      >
        <Toolbar sx={navToolbarSx}>
          {useDrawerNav ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    justifyContent: 'flex-start',
                  }}
                >
                  <Badge
                    color="error"
                    max={REVIEW_BADGE_MAX}
                    badgeContent={reviewBadgeContent}
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      flexShrink: 0,
                      ...drawerNavBadgeSx,
                    }}
                  >
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label={
                        reviewBadgeContent != null
                          ? `Menu, ${reviewBadgeContent} reviews due`
                          : 'Menu'
                      }
                      onClick={toggleDrawer(true)}
                      sx={{
                        flexShrink: 0,
                        width: DRAWER_NAV_MENU_BUTTON_PX,
                        height: DRAWER_NAV_MENU_BUTTON_PX,
                        p: 0,
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <MenuIcon sx={{ fontSize: DRAWER_NAV_MENU_ICON_PX }} />
                    </IconButton>
                  </Badge>
                </Box>
                <BrandLogo compact />
                <Box aria-hidden sx={{ flex: 1, minWidth: 0 }} />
              </Box>
              <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                slotProps={{ paper: { sx: drawerPaperSx } }}
              >
                {drawerMenuItems}
              </Drawer>
            </>
          ) : (
            <>
              <BrandLogo />
              {menuItems}
            </>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar
        sx={{
          minHeight: appBarHeight ?? APP_BAR_TOOLBAR_MIN_HEIGHT,
          height: appBarHeight ?? fallbackAppBarHeight,
        }}
      />
    </Box>
  );
};

export default TopMenu;
